import React from "react";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { SvgIcon } from "../../components/SvgComponents";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";


interface ReactbitsGlowInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

// Sub-component to render the slide content
const SlideContent: React.FC<{
  bullet: BulletItem;
  elapsedFrames: number;
  translateX: number;
  opacity: number;
  activeIndex: number;
  totalBullets: number;
  fps: number;
}> = ({ bullet, elapsedFrames, translateX, opacity, activeIndex, totalBullets, fps }) => {
  // SVG scale entrance animation
  const svgScale = spring({
    frame: elapsedFrames,
    fps,
    from: 0.2,
    to: 1.0,
    config: { damping: 14, mass: 0.6, stiffness: 100 },
  });

  // SVG floating animation (subtle vertical sinusoidal motion)
  const floatY = Math.sin(elapsedFrames * 0.04) * 12;

  // Words spring bounce animations
  const words = bullet.text.split(" ");
  
  // Highlight checker
  const isHighlighted = (word: string, highlights?: string[]) => {
    if (!highlights) return false;
    const cleanWord = word.replace(/[.,/#!$%^&*;:{}=\-_`~()?"']/g, "").toLowerCase();
    return highlights.some(
      (h) => cleanWord.includes(h.toLowerCase()) || h.toLowerCase().includes(cleanWord)
    );
  };

  const highlightStyle: React.CSSProperties = {
    fontWeight: 900,
    background: "linear-gradient(135deg, #00f2fe 30%, #bef264 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    textShadow: "0 0 15px rgba(0, 242, 254, 0.4)",
    display: "inline-block",
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 0 240px 0",
        boxSizing: "border-box",
        transform: `translateX(${translateX}px)`,
        opacity,
      }}
    >
      {/* 1. Large Animate SVG */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          flex: 1,
          maxHeight: "45%",
          transform: `scale(${svgScale}) translateY(${floatY}px)`,
          filter: "drop-shadow(0 20px 40px rgba(0, 242, 254, 0.25)) drop-shadow(0 4px 10px rgba(255, 0, 127, 0.15))",
          zIndex: 2,
        }}
      >
        {(() => {
          const brand = detectBrandInText(bullet.text);
          return brand ? (
            <CompanyBadge
              name={brand}
              color="#00f2fe"
              accentColor="#00f2fe"
              size={240}
              style="glow"
            />
          ) : (
            <SvgIcon
              name={bullet.icon || "undraw-astronaut-0o7w"}
              width={360}
              height={360}
              primaryColor="#00f2fe"
            />
          );
        })()}
      </div>

      {/* 2. Glassmorphic neon panel containing big typography */}
      <div
        style={{
          width: "100%",
          padding: "0 45px",
          boxSizing: "border-box",
          zIndex: 3,
        }}
      >
        <div
          style={{
            width: "100%",
            background: "rgba(6, 7, 19, 0.75)",
            backdropFilter: "blur(20px)",
            border: "1.5px solid rgba(0, 242, 254, 0.15)",
            borderRadius: "32px",
            boxShadow: "0 25px 60px rgba(0, 0, 0, 0.5), 0 0 20px rgba(0, 242, 254, 0.1)",
            padding: "36px",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            position: "relative",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {/* Card top bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div
              style={{
                fontFamily: "var(--font-title)",
                fontWeight: 800,
                fontSize: "14px",
                color: "#ff007f",
                letterSpacing: "3px",
                textTransform: "uppercase",
              }}
            >
              Takeaway — 0{activeIndex + 1}
            </div>

            <div
              style={{
                fontFamily: "var(--font-title)",
                fontSize: "16px",
                fontWeight: 800,
                color: "rgba(255, 255, 255, 0.4)",
                letterSpacing: "1px",
              }}
            >
              0{activeIndex + 1} / 0{totalBullets}
            </div>
          </div>

          {/* Big Typography with word-by-word spring pop-in */}
          <div
            style={{
              minHeight: "260px",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              alignContent: "center",
            }}
          >
            <p
              style={{
                fontFamily: "var(--font-title)",
                fontSize: "48px",
                fontWeight: 700,
                lineHeight: 1.35,
                color: "#ffffff",
                textAlign: "left",
                letterSpacing: "-0.5px",
                margin: 0,
                display: "flex",
                flexWrap: "wrap",
              }}
            >
              {words.map((word, idx) => {
                const wordStartFrame = idx * 1.5;
                const wordElapsed = Math.max(0, elapsedFrames - wordStartFrame);

                const wordScale = spring({
                  frame: wordElapsed,
                  fps,
                  from: 0,
                  to: 1,
                  config: { damping: 13, mass: 0.5, stiffness: 120 },
                });

                const wordTranslateY = interpolate(wordElapsed, [0, 8], [24, 0], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                });

                const wordOpacity = interpolate(wordElapsed, [0, 6], [0, 1], {
                  extrapolateLeft: "clamp",
                  extrapolateRight: "clamp",
                });

                return (
                  <span
                    key={idx}
                    style={{
                      display: "inline-block",
                      marginRight: "14px",
                      transform: `scale(${wordScale}) translateY(${wordTranslateY}px)`,
                      opacity: wordOpacity,
                    }}
                  >
                    {isHighlighted(word, bullet.highlights) ? (
                      <span style={highlightStyle}>{word}</span>
                    ) : (
                      <span>{word}</span>
                    )}
                  </span>
                );
              })}
            </p>
          </div>

          {/* Card bottom progress indicator line */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              height: "4px",
              width: `${((activeIndex + 1) / totalBullets) * 100}%`,
              background: "linear-gradient(90deg, #00f2fe, #ff007f)",
              boxShadow: "0 -2px 10px rgba(0, 242, 254, 0.4)",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const ReactbitsGlowInsights: React.FC<ReactbitsGlowInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps, width } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Identify which bullet insight is currently active in time
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs
  );

  // Fallback to ensure we are always displaying an active insight
  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Slide transition configurations
  const transitionFrames = 12;
  const isInTransition = elapsedFrames < transitionFrames && activeIndex > 0;

  // Compute positions/opacities for transitions
  let currentSlideX = 0;
  let currentSlideOpacity = 1;

  if (isInTransition) {
    const progress = elapsedFrames / transitionFrames; // 0 to 1
    currentSlideX = width * (1 - progress); // slide in from right
    currentSlideOpacity = progress;
  }

  // Sweep transition diagonal overlay
  const sweepProgress = interpolate(elapsedFrames, [0, transitionFrames], [0, 100]);
  const sweepX = interpolate(sweepProgress, [0, 100], [-120, 120]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flex: 1,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Slide Pagination Dots */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          paddingTop: "24px",
          zIndex: 10,
        }}
      >
        {bullets.map((_, idx) => (
          <div
            key={idx}
            style={{
              width: idx === activeIndex ? 32 : 10,
              height: 10,
              borderRadius: 5,
              background:
                idx === activeIndex
                  ? "linear-gradient(90deg, #00f2fe, #ff007f)"
                  : "rgba(255, 255, 255, 0.2)",
              boxShadow:
                idx === activeIndex ? "0 0 10px rgba(0, 242, 254, 0.4)" : "none",
            }}
          />
        ))}
      </div>

      {/* Render Slide Content */}
      <div style={{ flex: 1, position: "relative", width: "100%" }}>
        {/* Render outgoing slide if transitioning */}
        {isInTransition && (
          <SlideContent
            key={activeIndex - 1}
            bullet={bullets[activeIndex - 1]}
            elapsedFrames={elapsedFrames + (bullets[activeIndex].startMs - bullets[activeIndex - 1].startMs) / 1000 * fps}
            translateX={-width * (elapsedFrames / transitionFrames)} // slide out to left
            opacity={1 - elapsedFrames / transitionFrames}
            activeIndex={activeIndex - 1}
            totalBullets={bullets.length}
            fps={fps}
          />
        )}

        {/* Render active slide */}
        <SlideContent
          key={activeIndex}
          bullet={activeBullet}
          elapsedFrames={elapsedFrames}
          translateX={currentSlideX}
          opacity={currentSlideOpacity}
          activeIndex={activeIndex}
          totalBullets={bullets.length}
          fps={fps}
        />

        {/* Kinetic diagonal neon swipe transition overlay */}
        {isInTransition && (
          <div
            style={{
              position: "absolute",
              top: -100,
              bottom: -100,
              width: "50%",
              background: "linear-gradient(90deg, transparent, rgba(0, 242, 254, 0.6), rgba(255, 0, 127, 0.6), transparent)",
              transform: `skewX(-20deg) translateX(${sweepX}%)`,
              pointerEvents: "none",
              zIndex: 20,
              boxShadow: "0 0 50px rgba(0, 242, 254, 0.4)",
            }}
          />
        )}
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
